import React, { useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import 'assets/css/MiniCalendar.css';
import 'assets/css/InstructorFullClassCalendar.css';
import {
  Badge,
  Box,
  Flex,
  Icon,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import Card from 'components/card/Card';
import {
  formatClassTimeLA,
  getInstructorCalendarClassesForDate,
  getInstructorCalendarClassTitle,
  getInstructorCalendarColorScheme,
} from 'utils/instructorCalendarDisplay';

const WEEKDAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const formatCalendarWeekday = (locale, date) => WEEKDAY_LABELS[date.getDay()];

const formatSelectedDate = (date) => {
  if (!(date instanceof Date)) return '';

  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

function CalendarClassBlock({ classData, onClassClick }) {
  const scheme = getInstructorCalendarColorScheme(classData);
  const hoverBorder = useColorModeValue('brand.500', 'brand.300');

  return (
    <Box
      className="instructor-calendar-class-block"
      role="button"
      tabIndex={0}
      data-testid={`instructor-calendar-class-${classData.id}`}
      data-calendar-status={scheme.status}
      w="100%"
      mt="4px"
      px="7px"
      py="5px"
      borderRadius="8px"
      borderLeft="4px solid"
      style={{
        backgroundColor: scheme.bg,
        borderLeftColor: scheme.borderColor,
        color: scheme.color,
      }}
      textAlign="left"
      cursor="pointer"
      transition="all 0.15s ease"
      _hover={{ borderColor: hoverBorder, transform: 'translateY(-1px)' }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClassClick?.(classData);
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.stopPropagation();
        onClassClick?.(classData);
      }}
    >
      <Text
        className="instructor-calendar-class-time"
        as="span"
        display="block"
        fontSize="10px"
        fontWeight="900"
        lineHeight="1"
      >
        {formatClassTimeLA(classData.Date)}
      </Text>
      <Text
        className="instructor-calendar-class-title"
        as="span"
        display="block"
        fontSize="11px"
        fontWeight="800"
        lineHeight="1.15"
        noOfLines={2}
        mt="3px"
      >
        {getInstructorCalendarClassTitle(classData)}
      </Text>
    </Box>
  );
}

function MobileCalendarClassCard({ classData, onClassClick }) {
  const scheme = getInstructorCalendarColorScheme(classData);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const hoverBorder = useColorModeValue('brand.500', 'brand.300');

  return (
    <Flex
      className="instructor-calendar-mobile-class-card"
      role="button"
      tabIndex={0}
      data-testid={`mobile-instructor-calendar-class-${classData.id}`}
      data-calendar-status={scheme.status}
      border="1px solid"
      borderLeft="5px solid"
      borderColor="gray.200"
      borderLeftColor={scheme.borderColor}
      borderRadius="12px"
      p="12px"
      gap="8px"
      direction="column"
      cursor="pointer"
      style={{
        backgroundColor: scheme.bg,
        color: scheme.color,
      }}
      _hover={{ borderColor: hoverBorder, transform: 'translateY(-1px)' }}
      transition="all 0.15s ease"
      onClick={() => onClassClick?.(classData)}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onClassClick?.(classData);
      }}
    >
      <Flex justify="space-between" gap="10px" align="flex-start">
        <Box minW="0">
          <Text color={textColor} fontSize="sm" fontWeight="900" noOfLines={1}>
            {getInstructorCalendarClassTitle(classData)}
          </Text>
          <Text color={mutedText} fontSize="xs" fontWeight="700" mt="4px">
            {formatClassTimeLA(classData.Date)}
          </Text>
        </Box>
        <Badge colorScheme={scheme.status === 'needs-attention' ? 'red' : scheme.status === 'complete' ? 'gray' : 'green'}>
          {scheme.label}
        </Badge>
      </Flex>
    </Flex>
  );
}

export default function InstructorFullClassCalendar({
  classes = [],
  onClassClick,
  onMonthChange,
  onDateChange,
  value: controlledValue,
  ...rest
}) {
  const [value, setValue] = useState(controlledValue || new Date());
  const [activeStartDate, setActiveStartDate] = useState(controlledValue || new Date());
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const selectedDate = value instanceof Date ? value : new Date();
  const selectedDateClasses = getInstructorCalendarClassesForDate(classes, selectedDate);

  const handleDateChange = (nextValue) => {
    setValue(nextValue);
    onDateChange?.(nextValue);
  };

  const handleMonthChange = ({ activeStartDate: nextActiveStartDate }) => {
    setActiveStartDate(nextActiveStartDate);
    onMonthChange?.(nextActiveStartDate);
  };

  const desktopTileContent = ({ date, view }) => {
    if (view !== 'month') return null;

    const dateClasses = getInstructorCalendarClassesForDate(classes, date);
    if (dateClasses.length === 0) return null;

    const visibleClasses = dateClasses.slice(0, 3);
    const hiddenCount = dateClasses.length - visibleClasses.length;

    return (
      <Box className="instructor-calendar-tile-content" w="100%" mt="2px">
        {visibleClasses.map((classData) => (
          <CalendarClassBlock
            key={classData.id}
            classData={classData}
            onClassClick={onClassClick}
          />
        ))}
        {hiddenCount > 0 && (
          <Text color={mutedText} fontSize="10px" fontWeight="800" mt="4px" px="2px">
            +{hiddenCount} more
          </Text>
        )}
      </Box>
    );
  };

  const mobileTileContent = ({ date, view }) => {
    if (view !== 'month') return null;

    const dateClasses = getInstructorCalendarClassesForDate(classes, date);
    if (dateClasses.length === 0) return null;

    return (
      <Flex className="instructor-calendar-mobile-dots" aria-hidden="true">
        {dateClasses.slice(0, 4).map((classData) => {
          const scheme = getInstructorCalendarColorScheme(classData);

          return (
            <Box
              key={classData.id}
              className="instructor-calendar-mobile-dot"
              style={{ backgroundColor: scheme.borderColor }}
            />
          );
        })}
      </Flex>
    );
  };

  return (
    <Card p={{ base: '18px', md: '28px' }} w="100%" {...rest}>
      <Flex align="center" justify="space-between" mb="18px" gap="14px" wrap="wrap">
        <Box>
          <Text color={textColor} fontSize="xl" fontWeight="800">
            Class Calendar
          </Text>
          <Text color={mutedText} fontSize="sm" mt="4px">
            Click any class block to view details.
          </Text>
        </Box>
        <Flex gap="8px" wrap="wrap">
          <Badge colorScheme="green" borderRadius="8px" px="10px" py="4px">
            Upcoming
          </Badge>
          <Badge colorScheme="gray" borderRadius="8px" px="10px" py="4px">
            Complete
          </Badge>
          <Badge colorScheme="red" borderRadius="8px" px="10px" py="4px">
            Needs Attention
          </Badge>
        </Flex>
      </Flex>

      <Box className="instructor-full-calendar instructor-full-calendar-desktop">
        <Calendar
          onChange={handleDateChange}
          value={value}
          activeStartDate={activeStartDate}
          onActiveStartDateChange={handleMonthChange}
          selectRange={false}
          view="month"
          tileContent={desktopTileContent}
          calendarType="US"
          formatShortWeekday={formatCalendarWeekday}
          prevLabel={<Icon as={MdChevronLeft} w="24px" h="24px" mt="4px" />}
          nextLabel={<Icon as={MdChevronRight} w="24px" h="24px" mt="4px" />}
        />
      </Box>

      <Box className="instructor-full-calendar-mobile">
        <Box className="instructor-full-calendar instructor-full-calendar-mobile-calendar">
          <Calendar
            onChange={handleDateChange}
            value={value}
            activeStartDate={activeStartDate}
            onActiveStartDateChange={handleMonthChange}
            selectRange={false}
            view="month"
            tileContent={mobileTileContent}
            calendarType="US"
            formatShortWeekday={formatCalendarWeekday}
            prevLabel={<Icon as={MdChevronLeft} w="22px" h="22px" mt="4px" />}
            nextLabel={<Icon as={MdChevronRight} w="22px" h="22px" mt="4px" />}
          />
        </Box>

        <Box className="instructor-calendar-mobile-sidebar" mt="16px">
          <Text color={textColor} fontSize="md" fontWeight="900">
            {formatSelectedDate(selectedDate)}
          </Text>
          {selectedDateClasses.length === 0 ? (
            <Box mt="12px" p="18px" borderRadius="14px" bg="gray.50">
              <Text color={mutedText} fontSize="sm" fontWeight="700" textAlign="center">
                No classes scheduled
              </Text>
            </Box>
          ) : (
            <Flex direction="column" gap="10px" mt="12px">
              {selectedDateClasses.map((classData) => (
                <MobileCalendarClassCard
                  key={classData.id}
                  classData={classData}
                  onClassClick={onClassClick}
                />
              ))}
            </Flex>
          )}
        </Box>
      </Box>
    </Card>
  );
}
